import {useCallback, useMemo, useState} from 'react'
import {useGetLinesWithSearchQuery} from '@apis/base'
import {useGetProductionAreasQuery} from '@apis/settings'
import {useHierarchyEquipmentOptions} from '@hooks/useHierarchyFilters.ts'

export interface AppbarHierarchyFilterState {
  productionAreaId: string
  lineId: string
  equipmentId: string
}

export const EMPTY_APPBAR_HIERARCHY_FILTER_STATE: AppbarHierarchyFilterState = {
  productionAreaId: '',
  lineId: '',
  equipmentId: '',
}

export const APPBAR_HIERARCHY_FILTER_PARAM_KEYS = {
  PRODUCTION_AREA: 'productionAreaId',
  LINE: 'lineId',
  EQUIPMENT: 'equipmentId',
} as const

type Obj = {id: string; name: string}

interface UseAppbarHierarchyFilterReturn {
  filterState: AppbarHierarchyFilterState
  updateParam: (key: string, value: string) => void
  searchHandlers: {
    handleAreaSearch: (search: string) => void
    handleLineSearch: (search: string) => void
    handleEquipmentSearch: (search: string) => void
  }
  data: {productionAreas: Obj[]; lines: Obj[]; equipments: Obj[]}
  loading: {isFetchingAreas: boolean; isFetchingLines: boolean; isFetchingEquipments: boolean}
  errors: {areasError: unknown; linesError: unknown; equipmentsError: unknown}
  selectedNames: {
    selectedProductionArea: string
    selectedLine: string
    selectedEquipment: string
    selectedParentForEquipment: string
    mostSpecificSelection: string
  }
  paramKeys: typeof APPBAR_HIERARCHY_FILTER_PARAM_KEYS
}

// Backs the search popper's regional/local filter (AB#6170). Mirrors
// `useHierarchyFilters` (area -> line -> equipment cascade), but keeps its
// selection in component state supplied by the caller instead of URL params:
// the popper can be open on top of any page, each of which may already run its
// own `useHierarchyFilters` off the same param names, so writing this filter
// into the URL would collide with -- or be silently overwritten by -- the
// page's own filter. Plain state on the always-mounted `AppbarSearch` already
// satisfies the ticket's "persists across popper close/reopen" requirement
// without touching the URL.
export const useAppbarHierarchyFilter = (
  filterState: AppbarHierarchyFilterState,
  setFilterState: (next: AppbarHierarchyFilterState) => void,
): UseAppbarHierarchyFilterReturn => {
  const [searchState, setSearchState] = useState({area: '', line: ''})

  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = {...filterState, [key]: value}
      if (key === APPBAR_HIERARCHY_FILTER_PARAM_KEYS.PRODUCTION_AREA) {
        next.lineId = ''
        next.equipmentId = ''
      } else if (key === APPBAR_HIERARCHY_FILTER_PARAM_KEYS.LINE) {
        next.equipmentId = ''
      }
      setFilterState(next as AppbarHierarchyFilterState)
    },
    [filterState, setFilterState],
  )

  const {equipments, isFetchingEquipments, equipmentsError, handleEquipmentSearch} =
    useHierarchyEquipmentOptions({
      productionAreaId: filterState.productionAreaId,
      lineId: filterState.lineId,
      selectedEquipmentId: filterState.equipmentId || undefined,
    })

  const {
    data: productionAreasRaw,
    isFetching: isFetchingAreas,
    error: areasError,
  } = useGetProductionAreasQuery({search: searchState.area})

  const {
    data: lines = [],
    isFetching: isFetchingLines,
    error: linesError,
  } = useGetLinesWithSearchQuery({
    search: searchState.line,
    productionAreaId: filterState.productionAreaId,
    lean: true,
  })

  const productionAreas = useMemo(
    () =>
      Array.isArray(productionAreasRaw) ? productionAreasRaw : (productionAreasRaw?.results ?? []),
    [productionAreasRaw],
  )

  const selectedNames = useMemo(() => {
    const selectedProductionArea =
      productionAreas.find((area) => area.id === filterState.productionAreaId)?.name ?? ''
    const selectedLine = lines.find((line) => line.id === filterState.lineId)?.name ?? ''
    const selectedEquipment =
      equipments.find((equipment) => equipment.id === filterState.equipmentId)?.name ?? ''
    return {
      selectedProductionArea,
      selectedLine,
      selectedEquipment,
      selectedParentForEquipment: selectedLine || selectedProductionArea,
      // Most-specific selection first, matching the ticket's "narrows the
      // search" call: an equipment scope implies its line/area, a line scope
      // implies its area.
      mostSpecificSelection: selectedEquipment || selectedLine || selectedProductionArea,
    }
  }, [productionAreas, lines, equipments, filterState])

  return {
    filterState,
    updateParam,
    searchHandlers: {
      handleAreaSearch: (area: string): void => setSearchState((prev) => ({...prev, area})),
      handleLineSearch: (line: string): void => setSearchState((prev) => ({...prev, line})),
      handleEquipmentSearch,
    },
    data: {productionAreas, lines, equipments},
    loading: {isFetchingAreas, isFetchingLines, isFetchingEquipments},
    errors: {areasError, linesError, equipmentsError},
    selectedNames,
    paramKeys: APPBAR_HIERARCHY_FILTER_PARAM_KEYS,
  }
}
